import React, { memo, useMemo, useState } from 'react';
import { Popover } from 'antd';
import { EditOutlined, SmileOutlined } from '@ant-design/icons';
import MessageReactions from './MessageReactions';
import {
  REACTION_OPTIONS,
  formatMessageDateTime,
  formatRoomInfoContent,
} from '../../chatMessageUtils';
import ChatMessageContent from './ChatMessageContent';
import './MessageItem.css';
import './MessageReactions.css';

const MessageItem = memo(({
    message,
    isOutgoing,
    showAuthorMeta,
    startOfRun,
    currentUserId,
    roomMembers = [],
    onEdit,
    onReact,
    isReacting = false,
}) => {
    const displayContent = useMemo(() => {
        if (!message.roomInfo) {
            return message.content;
        }

        return formatRoomInfoContent(
            message.content,
            roomMembers,
            currentUserId,
        );
    }, [currentUserId, message.content, message.roomInfo, roomMembers]);

    const dateTime = useMemo(
        () => formatMessageDateTime(message.timestamp),
        [message.timestamp],
    );

    const dateTimeIso = useMemo(
        () => new Date(message.timestamp).toISOString(),
        [message.timestamp],
    );

    const senderInitial = useMemo(() => {
        const label = message.senderName || message.sender || '?';
        return String(label).charAt(0).toUpperCase();
    }, [message.senderName, message.sender]);

    const [isReactionPickerOpen, setIsReactionPickerOpen] = useState(false);

    if (message.roomInfo) {
        return (
            <div className={`message-row message-row--system ${startOfRun ? 'message-row--run-start' : ''}`}>
                <p className="message-row__system-text">{displayContent}</p>
            </div>
        );
    }

    const isEdited = Boolean(message.edited);

    const authorLabel = [
        message.senderName || message.sender,
        message.jobTitle ? `(${message.jobTitle})` : null,
    ]
        .filter(Boolean)
        .join(' ');

    const handleReact = (reaction) => {
        onReact?.(message, reaction);
    };

    const reactionPicker = (
        <div className="message-reactions-picker" role="menu" aria-label="Choose reaction">
            {REACTION_OPTIONS.map((reaction) => (
                <button
                    key={reaction}
                    type="button"
                    className="message-reactions-picker__option"
                    onClick={() => {
                        handleReact(reaction);
                        setIsReactionPickerOpen(false);
                    }}
                    disabled={isReacting}
                    aria-label={`React with ${reaction}`}
                >
                    {reaction}
                </button>
            ))}
        </div>
    );

    const reactions = (
        <MessageReactions
            reactions={message.reactions}
            currentUserId={currentUserId}
            isOutgoing={isOutgoing}
            onReact={handleReact}
            disabled={isReacting}
        />
    );

    const reactionButton = (
        <Popover
            open={isReactionPickerOpen}
            onOpenChange={setIsReactionPickerOpen}
            content={reactionPicker}
            trigger="click"
            placement={isOutgoing ? 'topRight' : 'topLeft'}
            overlayClassName="message-reactions-popover"
        >
            <button
                type="button"
                className="message-row__action-button"
                disabled={isReacting}
                aria-label="Add reaction"
            >
                <SmileOutlined />
            </button>
        </Popover>
    );

    const editButton = isOutgoing ? (
        <button
            type="button"
            className="message-row__action-button"
            onClick={() => onEdit?.(message)}
            aria-label="Edit message"
        >
            <EditOutlined />
        </button>
    ) : null;

    const messageFooter = (
        <div className="message-row__bubble-footer">
            {isEdited ? (
                <span className="message-row__edited">edited</span>
            ) : null}
            <time
                className={`message-row__time ${isOutgoing ? 'message-row__time--outgoing' : 'message-row__time--incoming'}`}
                dateTime={dateTimeIso}
            >
                {dateTime}
            </time>
        </div>
    );

    const messageActions = (
        <div className="message-row__bubble-actions">
            {reactionButton}
            {editButton}
        </div>
    );

    const bubbleClassName = isOutgoing
        ? 'message-row__outgoing-bubble'
        : 'message-row__incoming-bubble';

    const messageBubble = (
        <div className={bubbleClassName}>
            <div className="message-row__bubble-body">
                <ChatMessageContent content={message.content} />
                {messageActions}
            </div>
            {messageFooter}
        </div>
    );

    if (isOutgoing) {
        return (
            <div className={`message-row message-row--outgoing ${startOfRun ? 'message-row--run-start' : ''}`}>
                <div className="message-row__outgoing-shell">
                    {messageBubble}
                    {reactions}
                </div>
            </div>
        );
    }

    return (
        <div className={`message-row message-row--incoming ${startOfRun ? 'message-row--run-start' : ''}`}>
            <div className="message-row__incoming-shell">
                {showAuthorMeta ? (
                    <div className="message-row__incoming-meta">
                        <div className="message-row__avatar">{senderInitial}</div>
                        <span className="message-row__author">{authorLabel}</span>
                    </div>
                ) : null}
                {messageBubble}
                {reactions}
            </div>
        </div>
    );
}, (prev, next) =>
    prev.message.messageId === next.message.messageId &&
    prev.message.jobTitle === next.message.jobTitle &&
    prev.message.content === next.message.content &&
    prev.message.timestamp === next.message.timestamp &&
    prev.message.edited === next.message.edited &&
    prev.message.roomInfo === next.message.roomInfo &&
    JSON.stringify(prev.message.reactions) === JSON.stringify(next.message.reactions) &&
    prev.roomMembers === next.roomMembers &&
    prev.currentUserId === next.currentUserId &&
    prev.isReacting === next.isReacting &&
    prev.isOutgoing === next.isOutgoing &&
    prev.showAuthorMeta === next.showAuthorMeta &&
    prev.startOfRun === next.startOfRun,
);

MessageItem.displayName = 'MessageItem';

export default MessageItem;
